import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { useAdminSubcategories } from '@/presentation/hook/useAdminSubcategories';
import { useAdminCategories } from '@/presentation/hook/useAdminCategories';
import { Btn, Alert, Modal, InputField, MultilineText } from '@/presentation/component/ui';
import { inputClass } from '@/presentation/component/formStyles';
import { formatCFA } from '@/presentation/util/format';
import { truncate } from '@/presentation/util/text';
import { AdminBreadcrumb } from '@/presentation/component/admin/AdminBreadcrumb';
import type { AdminSubcategory } from '@/domain/model/AdminSubcategory';

interface SubcategoryFormData {
  name: string;
  description: string;
  categoryId: string;
  price: number;
  displayOrder: number;
}

const EMPTY_FORM: SubcategoryFormData = {
  name: '',
  description: '',
  categoryId: '',
  price: 0,
  displayOrder: 0,
};

export const AdminSubcategoriesPage = () => {
  const { t } = useTranslation();
  const { categoryId: parentId } = useParams<{ categoryId: string }>();
  const navigate = useNavigate();
  const isDrillDown = !!parentId;

  const {
    subcategories,
    isLoading,
    error,
    fetchSubcategories,
    createSubcategory,
    updateSubcategory,
    deleteSubcategory,
    clearError,
  } = useAdminSubcategories();
  const { categories, fetchCategories } = useAdminCategories();

  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingSubcategory, setEditingSubcategory] = useState<AdminSubcategory | null>(null);
  const [formData, setFormData] = useState<SubcategoryFormData>(EMPTY_FORM);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const activeFilter = isDrillDown ? parentId : selectedCategoryId;
  const parentCategory = isDrillDown ? categories.find((c) => c.id === parentId) : null;

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    fetchSubcategories(activeFilter || undefined);
  }, [fetchSubcategories, activeFilter]);

  const handleCategoryFilter = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedCategoryId(e.target.value);
  };

  const handleRowClick = (subcategoryId: string) => {
    navigate(`/admin/subcategories/${subcategoryId}/chapters`);
  };

  const openCreateForm = () => {
    setEditingSubcategory(null);
    setFormData({ ...EMPTY_FORM, ...(parentId ? { categoryId: parentId } : {}) });
    setIsFormOpen(true);
  };

  const openEditForm = (sub: AdminSubcategory) => {
    setEditingSubcategory(sub);
    setFormData({
      name: sub.name,
      description: sub.description,
      categoryId: sub.categoryId,
      price: sub.price,
      displayOrder: sub.displayOrder,
    });
    setIsFormOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    let success: boolean;
    if (editingSubcategory) {
      success = await updateSubcategory(editingSubcategory.id, formData);
      if (success) {
        toast.success(t('admin.subcategoryUpdated'));
      }
    } else {
      success = await createSubcategory(formData);
      if (success) {
        toast.success(t('admin.subcategoryCreated'));
      }
    }
    if (success) {
      setIsFormOpen(false);
      setEditingSubcategory(null);
      setFormData(EMPTY_FORM);
      fetchSubcategories(activeFilter || undefined);
    }
  };

  const handleConfirmDelete = async () => {
    if (deletingId) {
      const success = await deleteSubcategory(deletingId);
      if (success) {
        toast.success(t('admin.subcategoryDeleted'));
        fetchSubcategories(activeFilter || undefined);
      } else {
        toast.error(t('admin.deleteSubcategoryConflict'));
      }
      setDeletingId(null);
    }
  };

  return (
    <div>
      {isDrillDown && (
        <AdminBreadcrumb
          items={[
            { label: t('admin.categories'), to: '/admin/categories' },
            { label: parentCategory?.name ?? '...' },
          ]}
        />
      )}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-ink)]">
            {t('admin.subcategoriesNav')}
          </h1>
          <p className="text-sm text-[var(--color-ink-faint)]">
            {subcategories.length} {t('admin.subcategories')}
          </p>
        </div>
        <Btn kind="primary" icon={<Plus className="h-4 w-4" />} onClick={openCreateForm}>
          {t('admin.addSubcategory')}
        </Btn>
      </div>

      {!isDrillDown && (
        <div className="mt-4">
          <label htmlFor="category-filter" className="sr-only">
            {t('admin.parentCategory')}
          </label>
          <select
            id="category-filter"
            value={selectedCategoryId}
            onChange={handleCategoryFilter}
            className={`${inputClass()} sm:w-64`}
          >
            <option value="">{t('admin.allCategories')}</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {error && (
        <Alert kind="bad" onClose={clearError} className="mt-4">
          {t(error)}
        </Alert>
      )}

      {isLoading && (
        <p className="mt-6 text-center text-[var(--color-ink-faint)]">{t('common.loading')}</p>
      )}

      {!isLoading && subcategories.length === 0 && !error && (
        <p className="mt-6 text-center text-[var(--color-ink-faint)]">
          {t('admin.noSubcategories')}
        </p>
      )}

      {subcategories.length > 0 && (
        <>
          <div className="mt-6 hidden overflow-x-auto md:block">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-[var(--color-line)] text-[var(--color-ink-faint)]">
                  <th className="pb-3 pr-4 font-medium">{t('admin.subcategoryName')}</th>
                  <th className="pb-3 pr-4 font-medium">{t('admin.subcategoryDescription')}</th>
                  {!isDrillDown && (
                    <th className="pb-3 pr-4 font-medium">{t('admin.parentCategory')}</th>
                  )}
                  <th className="pb-3 pr-4 font-medium">{t('admin.subcategoryPrice')}</th>
                  <th className="pb-3 pr-4 font-medium">{t('admin.displayOrder')}</th>
                  <th className="pb-3 font-medium">{t('admin.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {subcategories.map((sub: AdminSubcategory) => (
                  <tr
                    key={sub.id}
                    role="button"
                    tabIndex={0}
                    aria-label={t('admin.viewChapters')}
                    className="cursor-pointer border-b border-[var(--color-line)] text-[var(--color-ink)] transition-colors hover:bg-[var(--color-surface-2)]"
                    onClick={() => handleRowClick(sub.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleRowClick(sub.id);
                      }
                    }}
                  >
                    <td className="py-3 pr-4 font-medium">{sub.name}</td>
                    <td className="py-3 pr-4">
                      <MultilineText className="text-[var(--color-ink-soft)]">
                        {truncate(sub.description, 60)}
                      </MultilineText>
                    </td>
                    {!isDrillDown && <td className="py-3 pr-4">{sub.categoryName}</td>}
                    <td className="py-3 pr-4">{formatCFA(sub.price)}</td>
                    <td className="py-3 pr-4">{sub.displayOrder}</td>
                    <td className="py-3">
                      <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                        <Btn
                          size="sm"
                          kind="ghost"
                          aria-label={t('admin.editSubcategory')}
                          onClick={() => openEditForm(sub)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Btn>
                        <Btn
                          size="sm"
                          kind="ghost"
                          aria-label={t('admin.deleteSubcategory')}
                          onClick={() => setDeletingId(sub.id)}
                        >
                          <Trash2 className="h-4 w-4 text-[var(--color-bad)]" />
                        </Btn>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-6 space-y-3 md:hidden">
            {subcategories.map((sub: AdminSubcategory) => (
              <div
                key={sub.id}
                role="button"
                tabIndex={0}
                aria-label={t('admin.viewChapters')}
                className="cursor-pointer rounded-xl border border-[var(--color-line)] bg-[var(--color-surface)] p-4 transition-colors hover:bg-[var(--color-surface-2)]"
                onClick={() => handleRowClick(sub.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleRowClick(sub.id);
                  }
                }}
              >
                <div className="flex items-start justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-[var(--color-ink)]">{sub.name}</p>
                    <MultilineText className="mt-1 text-sm text-[var(--color-ink-soft)]">
                      {sub.description}
                    </MultilineText>
                  </div>
                  <div className="ml-2 flex gap-1" onClick={(e) => e.stopPropagation()}>
                    <Btn
                      size="sm"
                      kind="ghost"
                      aria-label={t('admin.editSubcategory')}
                      onClick={() => openEditForm(sub)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Btn>
                    <Btn
                      size="sm"
                      kind="ghost"
                      aria-label={t('admin.deleteSubcategory')}
                      onClick={() => setDeletingId(sub.id)}
                    >
                      <Trash2 className="h-4 w-4 text-[var(--color-bad)]" />
                    </Btn>
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[var(--color-ink-faint)]">
                  {!isDrillDown && <span>{sub.categoryName}</span>}
                  <span>{formatCFA(sub.price)}</span>
                  <span>
                    {t('admin.displayOrder')}: {sub.displayOrder}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <Modal
        open={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        title={editingSubcategory ? t('admin.editSubcategory') : t('admin.addSubcategory')}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <InputField
            label={t('admin.subcategoryName')}
            placeholder={t('admin.subcategoryNamePlaceholder')}
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="subcategory-description"
              className="text-sm font-medium text-[var(--color-ink-soft)]"
            >
              {t('admin.subcategoryDescription')}
            </label>
            <textarea
              id="subcategory-description"
              className={inputClass()}
              placeholder={t('admin.subcategoryDescriptionPlaceholder')}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="subcategory-category"
              className="text-sm font-medium text-[var(--color-ink-soft)]"
            >
              {t('admin.parentCategory')} <span className="text-[var(--color-bad)]">*</span>
            </label>
            <select
              id="subcategory-category"
              value={formData.categoryId}
              onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
              className={inputClass()}
              required
              disabled={isDrillDown && !editingSubcategory}
            >
              <option value="">{t('admin.parentCategory')}</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>
          <InputField
            label={t('admin.subcategoryPrice')}
            type="number"
            min={0}
            placeholder={t('admin.subcategoryPricePlaceholder')}
            value={formData.price}
            onChange={(e) => setFormData({ ...formData, price: Number(e.target.value) })}
            required
          />
          <InputField
            label={t('admin.displayOrder')}
            type="number"
            min={0}
            value={formData.displayOrder}
            onChange={(e) => setFormData({ ...formData, displayOrder: Number(e.target.value) })}
            required
          />
          <div className="flex justify-end gap-2 pt-2">
            <Btn kind="ghost" type="button" onClick={() => setIsFormOpen(false)}>
              {t('admin.cancel')}
            </Btn>
            <Btn kind="primary" type="submit" disabled={isLoading} isLoading={isLoading}>
              {t('admin.save')}
            </Btn>
          </div>
        </form>
      </Modal>

      <Modal
        open={deletingId !== null}
        onClose={() => setDeletingId(null)}
        title={t('admin.confirmDelete')}
        size="sm"
      >
        <p className="text-sm text-[var(--color-ink-soft)]">
          {t('admin.confirmDeleteSubcategory')}
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Btn kind="ghost" onClick={() => setDeletingId(null)}>
            {t('admin.cancel')}
          </Btn>
          <Btn
            kind="primary"
            onClick={handleConfirmDelete}
            disabled={isLoading}
            isLoading={isLoading}
          >
            {t('admin.deleteSubcategory')}
          </Btn>
        </div>
      </Modal>
    </div>
  );
};
